import { PageHeaderTopRow, PageHeaderWrapper } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { ButtonInternalLink } from "../../components/ButtonInternalLink";
import { PullToRefresh } from "../../ShiftDiscovery/ListView/PullToRefresh";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { MY_SHIFTS_FULL_PATH } from "../../WorkerShifts/path";
import { CaseStatuses } from "../constants";
import { type CaseStatus } from "../types";
import { useGetCaseHistoryPageData } from "../useGetCaseHistoryPageData";
import { getBookedCasesQueryKey } from "../useGetCases";
import { CaseHistoryList } from "./List";
import { CaseHistoryListSkeleton } from "./Skeleton";
import { CaseHistoryTabs } from "./Tabs";

export function CaseHistoryContent() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const queryClient = useQueryClient();

  const [caseStatusFilter, setCaseStatusFilter] = useState<CaseStatus>(CaseStatuses.OPEN);

  const { bookedOpenCases, bookedClosedCases, isLoading } = useGetCaseHistoryPageData();
  useScrollToTopOnStatusTap(scrollRef);

  return (
    <Stack position="relative" sx={{ height: "100%" }}>
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow
          title="Case History"
          LinkComponent={ButtonInternalLink}
          navigateBack={() => {
            history.replace(MY_SHIFTS_FULL_PATH);
          }}
        />
      </PageHeaderWrapper>
      <CaseHistoryTabs
        caseStatusFilter={caseStatusFilter}
        setCaseStatusFilter={setCaseStatusFilter}
        openCasesCount={bookedOpenCases?.length}
        closedCasesCount={bookedClosedCases?.length}
      />
      <PullToRefresh
        scrollRef={scrollRef}
        onRefresh={async () => {
          await queryClient.invalidateQueries(getBookedCasesQueryKey);
        }}
      />

      <Stack
        ref={scrollRef}
        alignItems="center"
        spacing={10}
        sx={{
          flex: 1,
          overflow: "auto",
          px: 5,
        }}
      >
        {isLoading ? (
          <CaseHistoryListSkeleton />
        ) : (
          <CaseHistoryList
            cases={caseStatusFilter === CaseStatuses.OPEN ? bookedOpenCases : bookedClosedCases}
            caseStatusFilter={caseStatusFilter}
          />
        )}
      </Stack>
    </Stack>
  );
}
